import jsPDF from 'jspdf';
export interface RefoulementPDFData {
  numero_refoulement: string;
  date_refoulement: string;
  pesee_reference: string;
  agent_refoulement: string;
  motifs: Array<{
    libelle: string;
    description: string;
    categorie: string;
    criticite: string;
    code?: string;
  }>;
  observations?: string;
  peseeData: {
    reference: string;
    grossWeight: number;
    tareWeight: number;
    netWeight: number;
    vehicle: string;
    driver: string;
    product: string;
    site: string;
    grossAt: string;
    tareAt: string;
  };
}

export interface WeighingTicketPDFData {
  reference: string;
  grossWeight: number;
  tareWeight: number;
  netWeight: number;
  vehicle: string;
  driver: string;
  product: string;
  site: string;
  tenant: string;
  grossAt: string;
  tareAt: string;
  grossUser: string;
  tareUser: string;
  weighingDate?: string;
  campaignYear?: string;
  ticketNumber?: string;
  typePesee?: string; // Type de pesée (reception, expedition, transfert)
  supplierIdentity?: string;
  supplierCode?: string;
  clientCode?: string;
  clientCodeValue?: string;
  exporterName?: string;
  exporterCode?: string;
  chauffeurCode?: string;
  productNature?: string;
  productOrigin?: string;
  bushLotNumber?: string;
  trailerRegistration?: string;
  transferSheetNumber?: string;
  transporterName?: string;
  destination?: string;
  provenance?: string;
  stickerNumber?: string;
  firstWeighingWeight?: number;
  firstWeighingDateTime?: string;
  secondWeighingWeight?: number;
  secondWeighingDateTime?: string;
  humidityRateKOR?: string;
  qualityRefraction?: number;
  packagingType?: string;
  humidityRefraction?: number;
  bagWeight?: number;
  sacsCount?: {
    nb_sacs_declares?: number;
    nb_sacs_sains?: number;
    nb_sacs_recond?: number;
    nb_sacs_humides?: number;
    nb_sacs_dechires?: number;
    nb_sacs_rejetes?: number;
    nb_sacs_acceptes?: number;
  };
  qualityAnalysis?: {
    quality_decision?: string;
    quality_numbers?: string;
    moisture?: number;
    proteins?: number;
    hectoliter_weight?: number;
    impurities?: number;
    broken_grains?: number;
    germinated_grains?: number;
    observations?: string;
  };
  weighingAgentSignature?: string;
  clientSupplierSignature?: string;
}

export const generateRefoulementPDF = async (data: RefoulementPDFData): Promise<jsPDF> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;

  // Fonction pour ajouter du texte avec gestion de la pagination
  const addText = (text: string, x: number, y: number, options: any = {}) => {
    if (y > pageHeight - 20) {
      doc.addPage();
      yPosition = 20;
      return yPosition + 10;
    }
    doc.text(text, x, y);
    return y + (options.lineHeight || 7);
  };

  // Fonction pour dessiner une ligne
  const drawLine = (x1: number, y1: number, x2: number, y2: number) => {
    doc.line(x1, y1, x2, y2);
  };

  // Logo du Conseil du Coton et de l'Anacarde
  try {
    // Créer un canvas pour dessiner le logo
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const logoWidth = 80; // Réduit de 120 à 80
    const logoHeight = 25; // Réduit de 40 à 25
    const logoX = 15;
    const logoY = 5;
    
    // Configurer le canvas
    canvas.width = logoWidth;
    canvas.height = logoHeight;
    
    if (ctx) {
      // Fond blanc pour le logo
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, logoWidth, logoHeight);
      
      // Dessiner le logo CCA avec les éléments du SVG
      ctx.fillStyle = '#006241'; // Couleur verte du logo
      
      // Dessiner les éléments principaux du logo (simplifiés mais visibles)
      // Cercle principal plus petit
      ctx.beginPath();
      ctx.arc(15, 12, 6, 0, 2 * Math.PI);
      ctx.fill();
      
      // Texte du logo plus petit
      ctx.fillStyle = '#006241';
      ctx.font = 'bold 10px Arial';
      ctx.fillText('CONSEIL DU COTON', 30, 10);
      ctx.font = 'bold 10px Arial';
      ctx.fillText('ET DE L\'ANACARDE', 30, 20);
    }
    
    // Ajouter le logo au PDF
    const logoDataURL = canvas.toDataURL('image/png');
    doc.addImage(logoDataURL, 'PNG', logoX, logoY, logoWidth, logoHeight);
  } catch (error) {
    console.warn('Impossible de charger le logo, utilisation du texte:', error);
    // Fallback: utiliser du texte plus petit
    doc.setFontSize(10);
    doc.setTextColor(0, 100, 65); // Couleur verte du logo
    doc.text('CONSEIL DU COTON ET DE L\'ANACARDE', 15, 10);
    doc.setFontSize(8);
    doc.text('ORGANISATION DE REGULATION', 15, 18);
  }

  // Logo Pontcita à droite
  try {
    // Créer un canvas pour dessiner le logo Pontcita (simplifié)
    const pontcitaCanvas = document.createElement('canvas');
    const pontcitaCtx = pontcitaCanvas.getContext('2d');
    const pontcitaWidth = 40;
    const pontcitaHeight = 30;
    const pontcitaX = pageWidth - pontcitaWidth - 15;
    const pontcitaY = 5;
    
    pontcitaCanvas.width = pontcitaWidth;
    pontcitaCanvas.height = pontcitaHeight;
    
    if (pontcitaCtx) {
      // Fond blanc pour le logo
      pontcitaCtx.fillStyle = '#FFFFFF';
      pontcitaCtx.fillRect(0, 0, pontcitaWidth, pontcitaHeight);
      
      // Dessiner un logo simplifié (cercle orange avec texte)
      pontcitaCtx.fillStyle = '#FF8C00'; // Orange
      pontcitaCtx.beginPath();
      pontcitaCtx.arc(pontcitaWidth / 2, pontcitaHeight / 2, 12, 0, 2 * Math.PI);
      pontcitaCtx.fill();
      
      // Texte du logo
      pontcitaCtx.fillStyle = '#FFFFFF';
      pontcitaCtx.font = 'bold 10px Arial';
      pontcitaCtx.textAlign = 'center';
      pontcitaCtx.textBaseline = 'middle';
      pontcitaCtx.fillText('PC', pontcitaWidth / 2, pontcitaHeight / 2);
    }
    
    // Ajouter le logo au PDF
    const pontcitaDataURL = pontcitaCanvas.toDataURL('image/png');
    doc.addImage(pontcitaDataURL, 'PNG', pontcitaX, pontcitaY, pontcitaWidth, pontcitaHeight);
  } catch (error) {
    console.warn('Impossible de charger le logo Pontcita, utilisation du texte:', error);
    // Fallback: utiliser du texte
    doc.setFontSize(10);
    doc.setTextColor(255, 140, 0); // Couleur orange
    doc.text('PONTCITA', pageWidth - 50, 10, { align: 'right' });
  }

  // En-tête compact - ajusté pour le logo plus petit
  yPosition = 35; // Ajusté pour le logo plus petit (25px + 10px d'espace)
  doc.setFontSize(18);
  doc.setTextColor(220, 38, 38); // Rouge
  doc.text('TICKET DE REFOULEMENT', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 12;

  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text('PESEE REFOULEE', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Tampon REFOULE subtil et discret
  const stampX = pageWidth - 60;
  const stampY = 85; // Descendu pour le nouveau positionnement
  const stampSize = 35; // Taille réduite pour être moins apparent
  const radius = stampSize / 2;
  
  // Cercle extérieur du tampon avec effet grunge subtil
  doc.setDrawColor(180, 120, 80); // Rouge-brun plus clair et discret
  doc.setLineWidth(1.5);
  doc.circle(stampX, stampY, radius);
  
  // Cercle intérieur pour effet de relief subtil
  doc.setDrawColor(200, 140, 100);
  doc.setLineWidth(0.8);
  doc.circle(stampX, stampY, radius - 2);
  
  // Calculer les points de contact des traits avec le cercle intérieur
  const innerRadius = radius - 2; // Rayon du cercle intérieur
  const lineY1 = stampY - 1; // Position du trait supérieur (plus bas)
  const lineY2 = stampY + 3; // Position du trait inférieur (plus bas)

  // Calculer la largeur des traits pour qu'ils touchent le cercle intérieur
  const lineWidth1 = Math.sqrt(innerRadius * innerRadius - Math.pow(lineY1 - stampY, 2)) * 2;
  const lineWidth2 = Math.sqrt(innerRadius * innerRadius - Math.pow(lineY2 - stampY, 2)) * 2;

  // Traits qui rejoignent les bords intérieurs du cercle
  doc.setDrawColor(180, 120, 80);
  doc.setLineWidth(0.8);

  // Trait au-dessus de REFOULEE
  const lineStart1 = stampX - lineWidth1 / 2;
  const lineEnd1 = stampX + lineWidth1 / 2;
  doc.line(lineStart1, lineY1, lineEnd1, lineY1);

  // Trait en-dessous de REFOULEE
  const lineStart2 = stampX - lineWidth2 / 2;
  const lineEnd2 = stampX + lineWidth2 / 2;
  doc.line(lineStart2, lineY2, lineEnd2, lineY2);

  // Texte principal du tampon parfaitement centré
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold'); // Texte en gras
  doc.setTextColor(180, 120, 80);
  // Centrer verticalement entre les deux traits (encore plus bas)
  const textY = stampY + 2; // Centré entre les traits, encore plus bas
  doc.text('REFOULEE', stampX, textY, { align: 'center' });
  
  // Date du tampon discrète
  doc.setFontSize(5);
  doc.setFont('helvetica', 'normal'); // Remettre en normal pour la date
  doc.setTextColor(160, 100, 60);
  doc.text(new Date().toLocaleDateString('fr-FR'), stampX, stampY + 8, { align: 'center' });
  
  // Effet d'usure subtil (points plus petits et discrets)
  doc.setFillColor(200, 140, 100);
  doc.circle(stampX - 10, stampY - 8, 0.3, 'F');
  doc.circle(stampX + 10, stampY + 6, 0.2, 'F');
  doc.circle(stampX - 6, stampY + 10, 0.25, 'F');
  
  // Effet de transparence simulé avec des points plus clairs
  doc.setFillColor(220, 160, 120);
  doc.circle(stampX - 8, stampY - 6, 0.15, 'F');
  doc.circle(stampX + 8, stampY + 4, 0.1, 'F');

  // Numéro de refoulement
  doc.setFontSize(12);
  doc.setTextColor(220, 38, 38);
  doc.text(`N° ${data.numero_refoulement}`, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Informations de base compactes
      doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  yPosition = addText(`Date: ${new Date(data.date_refoulement).toLocaleDateString('fr-FR')}`, 20, yPosition);
  yPosition = addText(`Pesée: ${data.pesee_reference}`, 20, yPosition);
  yPosition = addText(`Agent: ${data.agent_refoulement}`, 20, yPosition);
  yPosition += 8;

  // Ligne de séparation
  drawLine(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 10;

  // Informations de la pesée compactes
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  yPosition = addText('INFORMATIONS DE LA PESEE', 20, yPosition);
  yPosition += 3;

  doc.setFontSize(9);
  yPosition = addText(`Référence: ${data.peseeData.reference}`, 20, yPosition);
  yPosition = addText(`Véhicule: ${data.peseeData.vehicle}`, 20, yPosition);
  yPosition = addText(`Chauffeur: ${data.peseeData.driver}`, 20, yPosition);
  yPosition = addText(`Produit: ${data.peseeData.product}`, 20, yPosition);
  yPosition = addText(`Site: ${data.peseeData.site}`, 20, yPosition);
  yPosition += 3;

  // Poids
  yPosition = addText(`Poids brut: ${data.peseeData.grossWeight.toLocaleString()} kg`, 20, yPosition);
  yPosition = addText(`Poids tare: ${data.peseeData.tareWeight.toLocaleString()} kg`, 20, yPosition);
  yPosition = addText(`Poids net: ${data.peseeData.netWeight.toLocaleString()} kg`, 20, yPosition);
  yPosition += 8;

  // Ligne de séparation
  drawLine(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 10;

  // Motifs de refoulement compacts
  doc.setFontSize(12);
  yPosition = addText('MOTIFS DE REFOULEMENT', 20, yPosition);
  yPosition += 3;

  doc.setFontSize(9);
  data.motifs.forEach((motif, index) => {
    yPosition = addText(`${index + 1}. ${motif.libelle || String(motif.id || '')}`, 20, yPosition);
    yPosition = addText(`   ${motif.description || 'Non disponible'}`, 25, yPosition);
    yPosition = addText(`   Catégorie: ${motif.categorie || 'Non définie'}`, 25, yPosition);
    if (motif.code) {
      yPosition = addText(`   Code: ${motif.code}`, 25, yPosition);
    }
    yPosition += 3;
  });

  yPosition += 8;

  // Observations compactes
  if (data.observations) {
    // Ligne de séparation
    drawLine(20, yPosition, pageWidth - 20, yPosition);
    yPosition += 8;

    doc.setFontSize(12);
    yPosition = addText('OBSERVATIONS', 20, yPosition);
    yPosition += 3;

    doc.setFontSize(9);
    const observations = data.observations.split('\n');
    observations.forEach(line => {
      yPosition = addText(line, 20, yPosition);
    });
    yPosition += 5;
  }

  // Pied de page compact
  const footerY = pageHeight - 15;
  doc.setFontSize(7);
  doc.setTextColor(128, 128, 128);
  doc.text(`Généré le ${new Date().toLocaleString('fr-FR')}`, pageWidth / 2, footerY, { align: 'center' });

  return doc;
};

export const generateWeighingTicketPDF = (data: WeighingTicketPDFData): jsPDF => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;

  // Fonction pour ajouter du texte avec gestion de la pagination
  const addText = (text: string, x: number, y: number, options: any = {}) => {
    if (y > pageHeight - 20) {
      doc.addPage();
      yPosition = 20;
      return yPosition + 10;
    }
    doc.text(text, x, y);
    return y + (options.lineHeight || 7);
  };

  // Fonction pour dessiner une ligne
  const drawLine = (x1: number, y1: number, x2: number, y2: number) => {
    doc.line(x1, y1, x2, y2);
  };

  // Logo du Conseil du Coton et de l'Anacarde
  try {
    // Créer un canvas pour dessiner le logo
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const logoWidth = 80;
    const logoHeight = 25;
    const logoX = 15;
    const logoY = 5;
    
    // Configurer le canvas
    canvas.width = logoWidth;
    canvas.height = logoHeight;
    
    if (ctx) {
      // Fond blanc pour le logo
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, logoWidth, logoHeight);
      
      // Dessiner le logo CCA avec les éléments du SVG
      ctx.fillStyle = '#006241'; // Couleur verte du logo
      
      // Dessiner les éléments principaux du logo (simplifiés mais visibles)
      // Cercle principal plus petit
      ctx.beginPath();
      ctx.arc(15, 12, 6, 0, 2 * Math.PI);
      ctx.fill();
      
      // Texte du logo plus petit
      ctx.fillStyle = '#006241';
      ctx.font = 'bold 10px Arial';
      ctx.fillText('CONSEIL DU COTON', 30, 10);
      ctx.font = 'bold 10px Arial';
      ctx.fillText('ET DE L\'ANACARDE', 30, 20);
    }
    
    // Ajouter le logo au PDF
    const logoDataURL = canvas.toDataURL('image/png');
    doc.addImage(logoDataURL, 'PNG', logoX, logoY, logoWidth, logoHeight);
  } catch (error) {
    console.warn('Impossible de charger le logo, utilisation du texte:', error);
    // Fallback: utiliser du texte plus petit
    doc.setFontSize(10);
    doc.setTextColor(0, 100, 65); // Couleur verte du logo
    doc.text('CONSEIL DU COTON ET DE L\'ANACARDE', 15, 10);
    doc.setFontSize(8);
    doc.text('ORGANISATION DE REGULATION', 15, 18);
  }

  // En-tête du ticket CAJOU - ajusté pour le logo
  yPosition = 35; // Ajusté pour le logo
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('CONSEIL DU COTON ET DE L\'ANACARDE', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;

  doc.setFontSize(11);
  doc.text('ORGANISATION DE REGULATION', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 20;

  // Titre principal
  doc.setFontSize(24);
  doc.setTextColor(0, 0, 0);
  doc.text('TICKET DE PESEE CAJOU', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Fonction pour obtenir le sous-titre selon le type de pesée
  const getTicketSubtitle = (typePesee?: string): string => {
    switch (typePesee?.toLowerCase()) {
      case 'reception':
        return 'BON RECEPTION';
      case 'expedition':
        return 'BON EXPEDITION';
      case 'transfert':
        return 'BON TRANSFERT';
      default:
        return 'BON RECEPTION'; // Par défaut
    }
  };

  doc.setFontSize(16);
  doc.text(getTicketSubtitle(data.typePesee), pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 20;

  // Numéro de ticket
  doc.setFontSize(16);
  doc.setTextColor(220, 38, 38);
  doc.text(`N° ${data.ticketNumber || data.reference}`, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 25;

  // Informations de base
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  yPosition = addText(`Date pesée: ${data.weighingDate || new Date(data.grossAt).toLocaleDateString('fr-FR')}`, 20, yPosition);
  yPosition = addText(`Campagne: ${data.campaignYear || new Date().getFullYear()}`, 20, yPosition);
  yPosition = addText(`Code pesée: ${data.reference}`, 20, yPosition);
  yPosition += 10;

  // Ligne de séparation
  drawLine(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 15;

  // Section Fournisseur
  doc.setFontSize(14);
  yPosition = addText('FOURNISSEUR', 20, yPosition);
  yPosition += 5;

  doc.setFontSize(10);
  yPosition = addText(`Identité: ${data.supplierIdentity || 'NOM DE L\'ENTITE'}${data.supplierCode ? `/${data.supplierCode}` : ''}`, 20, yPosition);
  yPosition = addText(`Client: ${data.clientCode || 'XXXX-XXXX'}${data.clientCodeValue ? `/${data.clientCodeValue}` : ''}`, 20, yPosition);
  yPosition = addText(`Exportateur: ${(data.exporterName || data.exporterCode) ? `${data.exporterName || ''}${data.exporterCode ? `/${data.exporterCode}` : ''}` : '01'}`, 20, yPosition);
  yPosition += 10;

  // Section Identification
  doc.setFontSize(14);
  yPosition = addText('IDENTIFICATION', 20, yPosition);
  yPosition += 5;

  doc.setFontSize(10);
  yPosition = addText(`Véhicule: ${data.vehicle}`, 20, yPosition);
  yPosition = addText(`Remorque: ${data.trailerRegistration || ''}`, 20, yPosition);
  yPosition = addText(`Chauffeur: ${data.driver}${data.chauffeurCode ? `/${data.chauffeurCode}` : ''}`, 20, yPosition);
  yPosition = addText(`Transporteur: ${data.transporterName || ''}`, 20, yPosition);
  yPosition = addText(`Provenance: ${data.provenance || ''}`, 20, yPosition);
  yPosition = addText(`Destination: ${data.destination || 'NOM DESTINATION'}`, 20, yPosition);
  yPosition += 10;

  // Section Poids
  doc.setFontSize(14);
  yPosition = addText('POIDS ET SACHERIE', 20, yPosition);
  yPosition += 5;

  doc.setFontSize(10);
  yPosition = addText(`Poids 1e Pesée: ${data.firstWeighingWeight ? data.firstWeighingWeight.toLocaleString() + ' kg' : 'Valeur Poids'}`, 20, yPosition);
  yPosition = addText(`Date 1: ${data.firstWeighingDateTime || 'xx/xx/xxxx xxhxx min'}`, 20, yPosition);
  yPosition = addText(`Poids 2e Pesée: ${data.secondWeighingWeight ? data.secondWeighingWeight.toLocaleString() + ' kg' : 'Valeur Poids'}`, 20, yPosition);
  yPosition = addText(`Date 2: ${data.secondWeighingDateTime || 'xx/xx/xxxx xxhxx min'}`, 20, yPosition);
  yPosition += 5;

  yPosition = addText(`Poids BRUT: ${data.grossWeight.toLocaleString()} kg`, 20, yPosition);
  yPosition = addText(`Poids TARE: ${data.tareWeight.toLocaleString()} kg`, 20, yPosition);
  yPosition = addText(`Poids NET: ${data.netWeight.toLocaleString()} kg`, 20, yPosition);
  yPosition += 10;

  // Comptage des sacs
  if (data.sacsCount) {
    doc.setFontSize(12);
    yPosition = addText('COMPTAGE DES SACS', 20, yPosition);
    yPosition += 5;

    doc.setFontSize(10);
    yPosition = addText(`Nbre Sacs Déclarés: ${data.sacsCount.nb_sacs_declares || 'XXXX'}`, 20, yPosition);
    yPosition = addText(`Nbre Sacs Sains: ${data.sacsCount.nb_sacs_sains || 'XXXX'}`, 20, yPosition);
    yPosition = addText(`Nbre Sacs Recond.: ${data.sacsCount.nb_sacs_recond || 'XXXX'}`, 20, yPosition);
    yPosition = addText(`Nbre sacs humides: ${data.sacsCount.nb_sacs_humides || 'XXXX'}`, 20, yPosition);
    yPosition = addText(`Nbre Sacs Déchirés: ${data.sacsCount.nb_sacs_dechires || 'XXXX'}`, 20, yPosition);
    yPosition += 10;
  }

  // Signatures
  yPosition += 20;
  drawLine(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  yPosition = addText('AGENT PESEUR', 50, yPosition);
  yPosition = addText('VISA CLIENT / FOURNISSEUR', pageWidth - 100, yPosition);
  yPosition += 20;

  // Lignes de signature
  drawLine(50, yPosition, 120, yPosition);
  drawLine(pageWidth - 120, yPosition, pageWidth - 50, yPosition);

  // Pied de page
  const footerY = pageHeight - 20;
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Généré le ${new Date().toLocaleString('fr-FR')}`, pageWidth / 2, footerY, { align: 'center' });

  return doc;
};

export const printPDF = (doc: jsPDF) => {
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};

export const downloadPDF = (doc: jsPDF, filename: string) => {
  doc.save(filename);
};

export const emailPDF = (doc: jsPDF, filename: string) => {
  const pdfBlob = doc.output('blob');
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};